import { useState } from "react";
import { writeBeliefsAI, bulkCreateBeliefs } from "@/lib/ai";
import { PageHeader } from "@/components/PageHeader";
import { Tag } from "@/components/Tag";
import { toast } from "sonner";
import { Sparkles, Check, X, Save } from "lucide-react";

export default function WriteBeliefsPage() {
  const [form, setForm] = useState({
    promotion_focus: "",
    holidays: "",
    custom_categories: "",
  });
  const [drafts, setDrafts] = useState([]);
  const [rejected, setRejected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    if (!form.promotion_focus.trim()) {
      toast.error("Tell the AI what you're promoting this month.");
      return;
    }
    setLoading(true);
    setDrafts([]);
    setRejected(new Set());
    try {
      const res = await writeBeliefsAI(form);
      setDrafts(res.drafts || []);
      toast.success(`Drafted ${res.drafts?.length || 0} beliefs. Approve the ones that land.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleReject = (id) => {
    setRejected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const editDraft = (id, patch) => {
    setDrafts((prev) =>
      prev.map((d) => (d.draft_id === id ? { ...d, ...patch } : d)),
    );
  };

  const saveApproved = async () => {
    const approved = drafts.filter((d) => !rejected.has(d.draft_id));
    if (approved.length === 0) {
      toast.error("Nothing approved to save.");
      return;
    }
    setSaving(true);
    try {
      const payload = approved.map((d) => ({
        belief_text: d.belief_text,
        category: d.category,
        offer_tags: d.offer_tags,
      }));
      const res = await bulkCreateBeliefs(payload);
      toast.success(`Saved ${res.created} new beliefs to your tree.`);
      setDrafts([]);
      setRejected(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const groupedDrafts = ["Why Me", "Why This", "Why Now", "Custom"].map((cat) => ({
    category: cat,
    items: drafts.filter((d) => d.category === cat),
  }));

  const approvedCount = drafts.length - rejected.size;

  return (
    <div data-testid="write-beliefs-page">
      <PageHeader
        eyebrow="AI assist"
        title="Write beliefs"
        subtitle="Tell the AI what you're promoting this month and the seasonal moments to lean into. It'll draft a fresh tree of beliefs in your voice — you approve the ones that land."
        testId="write-beliefs-header"
      />

      <div className="bg-white border border-[#141B4D]/15 rounded-sm p-6 md:p-8 mb-10 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
            What are you promoting this month? *
          </label>
          <textarea
            data-testid="promotion-focus-input"
            rows={3}
            value={form.promotion_focus}
            onChange={(e) =>
              setForm({ ...form, promotion_focus: e.target.value })
            }
            placeholder="e.g., TM Pro — done-for-you trademark package for entrepreneurs launching a new brand"
            className="w-full bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Holidays / seasonal moments (for Why Now beliefs)
            </label>
            <input
              data-testid="holidays-input"
              value={form.holidays}
              onChange={(e) => setForm({ ...form, holidays: e.target.value })}
              placeholder="e.g., Father's Day, Q4 launches, end of year"
              className="w-full bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Custom belief categories
            </label>
            <input
              data-testid="custom-categories-input"
              value={form.custom_categories}
              onChange={(e) =>
                setForm({ ...form, custom_categories: e.target.value })
              }
              placeholder="e.g., Why flat fees, Why DIY first"
              className="w-full bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            />
          </div>
        </div>

        <button
          onClick={generate}
          disabled={loading}
          data-testid="write-beliefs-generate-button"
          className="inline-flex items-center gap-2 bg-[#141B4D] text-[#F2E9DB] px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#141B4D]/90 disabled:opacity-60"
        >
          <Sparkles size={14} strokeWidth={2.5} />
          {loading ? "Writing your beliefs…" : "Write my beliefs"}
        </button>
      </div>

      {drafts.length > 0 && (
        <div className="space-y-8" data-testid="belief-drafts">
          <div className="flex items-center justify-between sticky top-16 bg-[#F2E9DB] z-10 py-4 border-b border-[#141B4D]/15">
            <div className="text-sm">
              <span className="font-bold text-[#141B4D]">{approvedCount}</span>{" "}
              <span className="text-[#141B4D]/70">of {drafts.length} drafts approved</span>
            </div>
            <button
              onClick={saveApproved}
              disabled={saving || approvedCount === 0}
              data-testid="save-approved-beliefs-button"
              className="inline-flex items-center gap-2 bg-[#FFD100] text-[#141B4D] px-5 py-2.5 rounded-sm text-sm font-bold hover:bg-[#e6bd00] disabled:opacity-60"
            >
              <Save size={14} strokeWidth={2.5} />
              {saving ? "Saving…" : `Save ${approvedCount} to my tree`}
            </button>
          </div>

          {groupedDrafts.map(({ category, items }) =>
            items.length === 0 ? null : (
              <section key={category} data-testid={`draft-group-${category.replace(/\s+/g, "-").toLowerCase()}`}>
                <h2 className="text-2xl font-bold text-[#141B4D] tracking-tight mb-4">
                  {category}
                </h2>
                <div className="space-y-3">
                  {items.map((d) => {
                    const isRejected = rejected.has(d.draft_id);
                    return (
                      <div
                        key={d.draft_id}
                        data-testid={`draft-belief-${d.draft_id}`}
                        className={`border rounded-sm p-5 transition-all ${
                          isRejected
                            ? "bg-[#141B4D]/5 border-[#141B4D]/10 opacity-50"
                            : "bg-white border-[#141B4D]/20"
                        }`}
                      >
                        <textarea
                          value={d.belief_text}
                          onChange={(e) =>
                            editDraft(d.draft_id, { belief_text: e.target.value })
                          }
                          rows={2}
                          disabled={isRejected}
                          data-testid={`draft-belief-text-${d.draft_id}`}
                          className="w-full bg-transparent text-sm text-[#141B4D] leading-relaxed border-0 focus:outline-none resize-none"
                        />
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#141B4D]/10">
                          <div className="flex flex-wrap gap-1.5">
                            {(d.offer_tags || []).map((t) => (
                              <Tag key={t}>{t}</Tag>
                            ))}
                          </div>
                          <button
                            onClick={() => toggleReject(d.draft_id)}
                            data-testid={`draft-toggle-${d.draft_id}`}
                            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-sm border ${
                              isRejected
                                ? "border-[#141B4D]/20 text-[#141B4D]/60 hover:bg-[#141B4D]/5"
                                : "border-emerald-300 bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            {isRejected ? (
                              <>
                                <X size={12} /> Skipped
                              </>
                            ) : (
                              <>
                                <Check size={12} /> Approved
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ),
          )}
        </div>
      )}
    </div>
  );
}
